import React, {useEffect, useState} from "react";
import {Alert, Badge, Button, Form, Spinner} from "react-bootstrap";
import {FaKey, FaPlus, FaTrash} from "react-icons/fa";
import {updateJobAdmin} from "../../service/JobService";
import {
    isSystemManagedChecklistItem,
    preserveSystemManagedChecklistItems,
} from "./checklistItemRules";

export default function JobChecklist({jobDetails, onJobUpdated}) {
    const [checklists, setChecklists] = useState(jobDetails?.checklists || []);
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");

    useEffect(() => {
        setChecklists(jobDetails?.checklists || []);
    }, [jobDetails]);

    function handleToggle(index) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.map((item, i) =>
            i === index ? {...item, completed: !item.completed} : item
        ));
    }

    function handleDescriptionChange(index, value) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.map((item, i) =>
            i === index ? {...item, description: value} : item
        ));
    }

    function handleLevelChange(index, value) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.map((item, i) =>
            i === index ? {...item, level: value} : item
        ));
    }

    function handleAdd() {
        setChecklists(prev => [...prev, {description: "", completed: false, level: "JOB"}]);
    }

    function handleRemove(index) {
        if (isSystemManagedChecklistItem(checklists[index])) return;
        setChecklists(prev => prev.filter((_, i) => i !== index));
    }

    function handleSave() {
        setIsSaving(true);
        setMessage("");
        const payload = {
            ...jobDetails,
            checklists: preserveSystemManagedChecklistItems(jobDetails?.checklists, checklists),
        };
        updateJobAdmin(payload).then(response => {
            if (response.status === 200) {
                response.json().then(result => {
                    setChecklists(result.checklists || []);
                    if (onJobUpdated) onJobUpdated(result);
                    setMessage("Checklist saved successfully");
                    setMessageType("success");
                });
            } else {
                response.text().then(result => {
                    setMessage(result || "Failed to save checklist");
                    setMessageType("danger");
                });
            }
        }).catch(error => {
            console.log('error', error);
            setMessage("An error occurred while saving.");
            setMessageType("danger");
        }).finally(() => setIsSaving(false));
    }

    const completedCount = checklists.filter(item => item.completed).length;

    return (
        <div style={{textAlign: "left"}}>
            <div style={{display: "flex", alignItems: "center", marginBottom: "12px"}}>
                {checklists.length > 0 && (
                    <Badge bg={completedCount === checklists.length ? "success" : "warning"} style={{marginRight: "12px"}}>
                        {completedCount}/{checklists.length} completed
                    </Badge>
                )}
                <Button variant="outline-primary" size="sm" onClick={handleAdd}>
                    <FaPlus style={{marginRight: "4px"}}/> Add Item
                </Button>
            </div>

            {checklists.length === 0 && (
                <Alert variant="info">No checklist items. Click "Add Item" to create one.</Alert>
            )}

            {checklists.map((item, index) => {
                const systemManaged = isSystemManagedChecklistItem(item);
                return (
                <div key={item.id || `new-${index}`}
                     style={{
                         display: "flex",
                         alignItems: "center",
                         padding: "8px 12px",
                         marginBottom: "4px",
                         borderRadius: "4px",
                         backgroundColor: item.completed ? "#f0fdf0" : "#fff",
                         border: "1px solid #dee2e6"
                     }}>
                    <Form.Check
                        type="checkbox"
                        checked={item.completed}
                        disabled={systemManaged}
                        aria-label={systemManaged ? "System-managed locker password status" : undefined}
                        onChange={() => handleToggle(index)}
                        style={{marginRight: "12px"}}
                    />
                    <Form.Control
                        type="text"
                        size="sm"
                        value={item.description}
                        readOnly={systemManaged}
                        placeholder="Checklist item description..."
                        onChange={(e) => handleDescriptionChange(index, e.target.value)}
                        style={{
                            flex: 1,
                            textDecoration: item.completed ? "line-through" : "none",
                            color: item.completed ? "#6c757d" : "#212529"
                        }}
                    />
                    <Form.Select
                        size="sm"
                        value={item.level}
                        disabled={systemManaged}
                        onChange={(e) => handleLevelChange(index, e.target.value)}
                        style={{width: "120px", marginLeft: "8px"}}
                    >
                        <option value="JOB">JOB</option>
                        <option value="PROPERTY">PROPERTY</option>
                    </Form.Select>
                    {systemManaged ? (
                        <Badge bg={item.completed ? "success" : "warning"} style={{marginLeft: "8px"}}>
                            <FaKey className="me-1"/>
                            {item.completed ? "Password updated" : "Worker entry required"}
                        </Badge>
                    ) : (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            aria-label="Remove checklist item"
                            onClick={() => handleRemove(index)}
                            style={{marginLeft: "8px"}}
                        >
                            <FaTrash/>
                        </Button>
                    )}
                </div>
            )})}

            <div style={{marginTop: "12px", display: "flex", alignItems: "center"}}>
                <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
                    {isSaving ? <>
                        <Spinner as="span" animation="border" size="sm" className="me-2"/>
                        Saving...
                    </> : "Save Checklist"}
                </Button>
                {message && (
                    <Alert variant={messageType} className="p-2 mb-0 ms-3" style={{display: "inline-block"}}>
                        {message}
                    </Alert>
                )}
            </div>
        </div>
    );
}
